import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import {
  Box,
  Paper,
  Typography,
  FormGroup,
  FormControlLabel,
  Switch,
  Stack,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import NotificationTemplatesManager from '../components/NotificationTemplatesManager';

type NotificationsConfig = {
  notifyNoDriverFound: boolean;
  notifyReadyForPickup: boolean;
  notifyOutForDelivery: boolean;
  notifyDeliveredDelivery: boolean;
  notifyDeliveredPickup: boolean;
};

const DEFAULTS: NotificationsConfig = {
  notifyNoDriverFound: true,
  notifyReadyForPickup: true,
  notifyOutForDelivery: true,
  notifyDeliveredDelivery: true,
  notifyDeliveredPickup: true,
};

const NotificationsSettings: React.FC = () => {
  const [config, setConfig] = useState<NotificationsConfig>(DEFAULTS);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<boolean>(false);

  const ref = doc(db, 'config', 'notifications');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const snap = await getDoc(ref);
        if (mounted) {
          if (snap.exists()) {
            const data = snap.data() as Partial<NotificationsConfig>;
            setConfig({
              notifyNoDriverFound: typeof data.notifyNoDriverFound === 'boolean' ? data.notifyNoDriverFound : DEFAULTS.notifyNoDriverFound,
              notifyReadyForPickup: typeof data.notifyReadyForPickup === 'boolean' ? data.notifyReadyForPickup : DEFAULTS.notifyReadyForPickup,
              notifyOutForDelivery: typeof data.notifyOutForDelivery === 'boolean' ? data.notifyOutForDelivery : DEFAULTS.notifyOutForDelivery,
              notifyDeliveredDelivery: typeof data.notifyDeliveredDelivery === 'boolean' ? data.notifyDeliveredDelivery : DEFAULTS.notifyDeliveredDelivery,
              notifyDeliveredPickup: typeof data.notifyDeliveredPickup === 'boolean' ? data.notifyDeliveredPickup : DEFAULTS.notifyDeliveredPickup,
            });
          } else {
            // Doc absent: afficher les valeurs par défaut. Le backend le créera aussi à la demande.
            setConfig(DEFAULTS);
          }
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || 'Erreur de chargement des paramètres');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleToggle = (key: keyof NotificationsConfig) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfig(prev => ({ ...prev, [key]: e.target.checked }));
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      // merge: true pour ne pas écraser d'autres champs éventuels
      await setDoc(ref, config, { merge: true });
      setSaved(true);
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Notifications client
      </Typography>

      {loading ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} /> <Typography>Chargement…</Typography>
        </Box>
      ) : (
        <>
          {error && (
            <Box mb={2}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {saved && !error && (
            <Box mb={2}>
              <Alert severity="success">Paramètres enregistrés.</Alert>
            </Box>
          )}

          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={config.notifyNoDriverFound}
                  onChange={handleToggle('notifyNoDriverFound')}
                />
              }
              label="Informer le client si aucun livreur n'est trouvé (NO_DRIVER_FOUND)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.notifyReadyForPickup}
                  onChange={handleToggle('notifyReadyForPickup')}
                />
              }
              label="Notifier 'Prête à récupérer' (pickup)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.notifyOutForDelivery}
                  onChange={handleToggle('notifyOutForDelivery')}
                />
              }
              label="Notifier 'En route' (livraison)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.notifyDeliveredDelivery}
                  onChange={handleToggle('notifyDeliveredDelivery')}
                />
              }
              label="Notifier 'Livrée' (livraison)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={config.notifyDeliveredPickup}
                  onChange={handleToggle('notifyDeliveredPickup')}
                />
              }
              label="Notifier 'Collectée' (pickup)"
            />
          </FormGroup>

          <Stack direction="row" spacing={2} mt={2}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button variant="outlined" onClick={() => setConfig(DEFAULTS)} disabled={saving}>
              Réinitialiser par défaut
            </Button>
          </Stack>

          <Box mt={3}>
            <NotificationTemplatesManager />
          </Box>
        </>
      )}
    </Paper>
  );
};

export default NotificationsSettings;
